// Recurrence date utilities

const WEEKDAY_LABELS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const POSITION_LABELS = {
  1: "First",
  2: "Second",
  3: "Third",
  4: "Fourth",
  "-1": "Last",
};

export const RECURRENCE_RULES = [
  { id: "daily", label: "Daily" },
  { id: "weekly", label: "Weekly" },
  { id: "monthly_date", label: "Monthly (same date)" },
  { id: "monthly_weekday", label: "Monthly (Nth weekday)" },
];

export function humanRule({ recurrence_rule, recurrence_position, recurrence_weekday }) {
  if (!recurrence_rule) return "";
  if (recurrence_rule === "daily") return "Every day";
  if (recurrence_rule === "weekly") return "Every week";
  if (recurrence_rule === "monthly_date") return "Same day of each month";
  if (recurrence_rule === "monthly_weekday") {
    const pos = POSITION_LABELS[String(recurrence_position)] || "First";
    const wd = WEEKDAY_LABELS[recurrence_weekday ?? 0];
    return `${pos} ${wd} of every month`;
  }
  return recurrence_rule;
}

function nthWeekdayOfMonth(year, month0, weekday, position) {
  // month0: 0..11, weekday: 0=Mon..6=Sun, position: 1..5 or -1 for last
  // Return a Date at midnight local time
  const daysInMonth = new Date(year, month0 + 1, 0).getDate();
  if (position === -1) {
    for (let d = daysInMonth; d >= 1; d--) {
      const dt = new Date(year, month0, d);
      const wd = (dt.getDay() + 6) % 7; // JS: Sun=0..Sat=6 → make Mon=0..Sun=6
      if (wd === weekday) return dt;
    }
    return null;
  }
  const firstWd = (new Date(year, month0, 1).getDay() + 6) % 7;
  const offset = (weekday - firstWd + 7) % 7;
  const day = 1 + offset + (position - 1) * 7;
  if (day > daysInMonth) return null;
  return new Date(year, month0, day);
}

function addMonths(date, n) {
  const d = new Date(date);
  const day = d.getDate();
  d.setDate(1);
  d.setMonth(d.getMonth() + n);
  const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  d.setDate(Math.min(day, lastDay));
  return d;
}

export function computeOccurrenceDates(baseStart, baseEnd, {
  recurrence_rule,
  recurrence_count,
  recurrence_position,
  recurrence_weekday,
}) {
  if (!baseStart || !baseEnd) return [];
  const start = new Date(baseStart);
  const end = new Date(baseEnd);
  if (isNaN(start) || isNaN(end)) return [];
  const duration = end - start;
  const count = Math.max(1, Math.min(52, Number(recurrence_count) || 1));
  const out = [];

  if (recurrence_rule === "daily") {
    for (let i = 0; i < count; i++) {
      const s = new Date(start); s.setDate(s.getDate() + i);
      out.push({ start_at: s, end_at: new Date(s.getTime() + duration) });
    }
  } else if (recurrence_rule === "weekly") {
    for (let i = 0; i < count; i++) {
      const s = new Date(start); s.setDate(s.getDate() + i * 7);
      out.push({ start_at: s, end_at: new Date(s.getTime() + duration) });
    }
  } else if (recurrence_rule === "monthly_date") {
    for (let i = 0; i < count; i++) {
      const s = addMonths(start, i);
      s.setHours(start.getHours(), start.getMinutes(), 0, 0);
      out.push({ start_at: s, end_at: new Date(s.getTime() + duration) });
    }
  } else if (recurrence_rule === "monthly_weekday") {
    const pos = Number(recurrence_position) || 1;
    const wd = Number(recurrence_weekday) || 0;
    let y = start.getFullYear();
    let m = start.getMonth();
    let safety = 0;
    while (out.length < count && safety < count * 3 + 12) {
      const d = nthWeekdayOfMonth(y, m, wd, pos);
      if (d) {
        d.setHours(start.getHours(), start.getMinutes(), 0, 0);
        // for first month, only include if not before start
        if (!(out.length === 0 && d < start)) {
          out.push({ start_at: d, end_at: new Date(d.getTime() + duration) });
        }
      }
      m++;
      if (m > 11) { m = 0; y++; }
      safety++;
    }
  }
  return out;
}

export function toLocalDatetimeInput(date) {
  if (!date) return "";
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d)) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export { WEEKDAY_LABELS, POSITION_LABELS };
